import { ProductType, ResponseProductAttribute } from '@/data/types/Product';
import { calculateProductPrices } from 'integration/utils/calculateProductPrices';

export type ExclusionRules = Record<string, string[]>;

export type ExclusionProduct = Pick<
	ProductType,
	'partNumber' | 'keyword' | 'manufacturer' | 'parentCatalogGroupID'
> & {
	attributes?: ResponseProductAttribute[];
	items?: { partNumber: string }[];
	productPrice?: Pick<ProductType['productPrice'], 'list' | 'offer'>;
	ribbons?: ProductType['ribbons'];
};

const PRODUCT_RULE = 'producto';
const BRAND_RULE = 'marca';
export const CATEGORY_RULE = 'categoria';
const DISCOUNT_RULE = 'descuento';
const DISCOUNT_RULE_ENABLED = 'si';
const BUILT_IN_RULES = [PRODUCT_RULE, BRAND_RULE, CATEGORY_RULE, DISCOUNT_RULE];

export const normalize = (value?: string) =>
	(value ?? '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.trim()
		.toLowerCase();

const toPlainLines = (html: string) =>
	html
		.replace(/<br\s*\/?>|<\/(p|div|li)>/gi, '\n')
		.replace(/<[^>]+>/g, '')
		.replace(/&nbsp;/gi, ' ')
		.replace(/&([a-z])(acute|grave|uml|tilde|circ);/gi, '$1')
		.split('\n');

export const parseExclusionRules = (html = ''): ExclusionRules =>
	toPlainLines(html).reduce<ExclusionRules>((rules, line) => {
		const separator = line.indexOf(':');
		const key = normalize(line.slice(0, separator));
		const values = line
			.slice(separator + 1)
			.split(',')
			.map(normalize)
			.filter(Boolean);
		return separator > 0 && key && values.length
			? { ...rules, [key]: [...(rules[key] ?? []), ...values] }
			: rules;
	}, {});

export const toCategoryIds = (parents?: string | string[]) => [
	...new Set(
		(Array.isArray(parents) ? parents : [parents ?? ''])
			.flatMap((path) => path.split('/'))
			.filter(Boolean)
	),
];

const productCodes = ({ partNumber, keyword, items }: ExclusionProduct) =>
	[partNumber, ...(keyword ?? '').split(','), ...(items ?? []).map((item) => item.partNumber)].map(
		normalize
	);

const attributeValues = (attributes: ResponseProductAttribute[] = [], rule: string) =>
	attributes
		.filter(({ identifier, name }) => normalize(identifier) === rule || normalize(name) === rule)
		.flatMap(({ values }) => (values ?? []).flatMap(({ value }) => [value].flat()))
		.map(normalize);

const isDiscounted = ({ productPrice, ribbons }: ExclusionProduct) => {
	const { shouldOffer, additionalOffer } = calculateProductPrices({
		list: productPrice?.list ?? 0,
		offer: productPrice?.offer ?? 0,
		ribbons: ribbons ?? [],
	});
	return shouldOffer || !!additionalOffer;
};

const matches = (expected: string[] | undefined, actual: string[]) =>
	!!expected?.some((value) => actual.includes(value));

export const isExcludedByRules = (
	rules: ExclusionRules,
	product: ExclusionProduct,
	categoryLabels: string[] = []
) =>
	matches(rules[PRODUCT_RULE], productCodes(product)) ||
	matches(rules[BRAND_RULE], [normalize(product.manufacturer)]) ||
	matches(rules[CATEGORY_RULE], categoryLabels) ||
	(!!rules[DISCOUNT_RULE]?.includes(DISCOUNT_RULE_ENABLED) && isDiscounted(product)) ||
	Object.entries(rules)
		.filter(([rule]) => !BUILT_IN_RULES.includes(rule))
		.some(([rule, values]) => matches(values, attributeValues(product.attributes, rule)));
